import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
} from '@/shared/components/ui/dropdown-menu';
import { ChevronDown } from 'lucide-react';
import { _t } from '@/i18n/t';
import { SUBSCRIPTION_MIN_WIDTH } from '../subscriptionShared';

interface SubscriptionStatusCardProps {
  planName: string;
  statusLabel: string;
  /** Colour utility classes for the badge; layout classes are added here. */
  statusBadgeClass: string;
  billingDetail: string;
  showManageControls: boolean;
  isCanceled: boolean;
  /** When true the "update payment" menu item is hidden (the banner covers it). */
  showPaymentBanner: boolean;
  isProcessing: boolean;
  onUpdatePayment: () => void;
  onCancel: () => void;
  onReactivate: () => void;
}

const SubscriptionStatusCard = ({
  planName,
  statusLabel,
  statusBadgeClass,
  billingDetail,
  showManageControls,
  isCanceled,
  showPaymentBanner,
  isProcessing,
  onUpdatePayment,
  onCancel,
  onReactivate,
}: SubscriptionStatusCardProps) => {
  const badgeClass = `inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${statusBadgeClass}`;

  return (
    <div
      className="border border-border bg-card rounded-md px-6 py-4 flex flex-wrap items-center justify-between gap-3 mb-8 shadow-sm"
      style={{ minWidth: SUBSCRIPTION_MIN_WIDTH }}
    >
      <div className="flex flex-col">
        <div className="flex items-center gap-2 mb-1">
          <span className="text-sm font-semibold text-foreground">{planName}</span>
          <span className={badgeClass}>{statusLabel}</span>
        </div>
        <p className="text-xs text-muted-foreground m-0">{billingDetail}</p>
      </div>

      {showManageControls && (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button
              data-th-button="true"
              className="inline-flex items-center justify-center gap-1.5 whitespace-nowrap rounded-md border border-transparent bg-primary px-3 py-1.5 text-sm font-medium text-primary-foreground transition-colors hover:bg-primary/90 disabled:pointer-events-none disabled:opacity-50"
              disabled={isProcessing}
            >
              {isProcessing
                ? _t('settings.subscription.processing')
                : _t('settings.subscription.managePlan')}
              <ChevronDown className="size-3.5" />
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="z-[1001]">
            {!showPaymentBanner && (
              <DropdownMenuItem onClick={onUpdatePayment}>
                {_t('settings.subscription.menu.updatePayment')}
              </DropdownMenuItem>
            )}
            {!isCanceled ? (
              <DropdownMenuItem variant="destructive" onClick={onCancel}>
                {_t('settings.subscription.menu.cancel')}
              </DropdownMenuItem>
            ) : (
              <DropdownMenuItem
                className="text-emerald-600 focus:text-emerald-600"
                onClick={onReactivate}
              >
                {_t('settings.subscription.menu.reactivate')}
              </DropdownMenuItem>
            )}
          </DropdownMenuContent>
        </DropdownMenu>
      )}
    </div>
  );
};

export default SubscriptionStatusCard;